import { ScrollArea } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import { Document } from '@tiptap/extension-document';
import FontFamily from '@tiptap/extension-font-family';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Table } from 'pages/revisions-module/template-editor/editors/template/extensions/Table';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorOptions, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { FEATURE_TOGGLE_REVISION_TEMPLATE_EDITOR_FONT_STYLE } from 'env';
import { debounce } from 'lodash';
import { useFormSections } from 'pages/revisions-module/template-editor/editors/form/section/FormSectionsDataProvider';
import { Color } from 'pages/revisions-module/template-editor/editors/template/extensions/Color';
import { DeviceLoop } from 'pages/revisions-module/template-editor/editors/template/extensions/device-loop/DeviceLoop';
import { FontSize } from 'pages/revisions-module/template-editor/editors/template/extensions/FontSize';
import { HtmlBlock } from 'pages/revisions-module/template-editor/editors/template/extensions/HtmlBlock';
import { IfBlock } from 'pages/revisions-module/template-editor/editors/template/extensions/if-block/IfBlock';
import IfBlockBubbleMenu from 'pages/revisions-module/template-editor/editors/template/extensions/if-block/IfBlockBubbleMenu';
import DeviceLoopBubbleMenu from 'pages/revisions-module/template-editor/editors/template/extensions/device-loop/DeviceLoopBubbleMenu';
import TableMergeBubbleMenu from 'pages/revisions-module/template-editor/editors/template/extensions/table-merge/TableMergeBubbleMenu';
import { PageBreak } from 'pages/revisions-module/template-editor/editors/template/extensions/PageBreak';
import { AlignBottom } from 'pages/revisions-module/template-editor/editors/template/extensions/AlignBottom';
import {
  HeaderFooterSide,
  PageBody,
  PageFooter,
  PageHeader,
} from 'pages/revisions-module/template-editor/editors/template/extensions/PageSections';
import usePlaceholderConfigurator from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/configurator/PlaceholderConfigurator';
import { DeviceFaultsPlaceholder } from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/device-faults/DeviceFaultsPlaceholder';
import useDeviceFaultsPlaceholderConfigurator from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/device-faults/DeviceFaultsPlaceholderConfigurator';
import { DeviceMeasurementsPlaceholder } from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/device-measurements/DeviceMeasurementsPlaceholder';
import useDeviceMeasurementsPlaceholderConfigurator from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/device-measurements/DeviceMeasurementsPlaceholderConfigurator';
import {
  BlockPlaceholder,
  InlinePlaceholder,
} from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/Placeholder';
import { TableCell } from 'pages/revisions-module/template-editor/editors/template/extensions/TableCell';
import { StripFontFamilyOnPaste } from 'pages/revisions-module/template-editor/editors/template/extensions/StripFontFamilyOnPaste';
import { Tabulator } from 'pages/revisions-module/template-editor/editors/template/extensions/Tabulator';
import { useEditorStyles } from 'pages/revisions-module/template-editor/editors/template/styles/use-editor-styles';
import { useInjectFieldNameStyles } from 'pages/revisions-module/template-editor/editors/template/styles/use-inject-field-name-styles';
import { useInjectSectionNameStyles } from 'pages/revisions-module/template-editor/editors/template/styles/use-inject-section-name-styles';
import Toolbar from 'pages/revisions-module/template-editor/editors/template/toolbar/Toolbar';
import { shouldLoadGoogleFont } from 'pages/revisions-module/template-editor/editors/visual/typography/google-font-utils';
import { useTemplateEditorData } from 'pages/revisions-module/template-editor/TemplateEditorDataProvider';
import { useMemo, useRef } from 'react';

/**
 * The template editor part of the template editor page.
 */
export default function TemplateEditor() {
  const editorRef = useRef<HTMLDivElement>(null);
  const { sections } = useFormSections();
  const { visual } = useTemplateEditorData();
  const { template, setTemplate } = useTemplateEditorData();
  const { classes } = useEditorStyles(visual);
  const injectFieldNameStyles = useInjectFieldNameStyles();
  const injectSectionNameStyles = useInjectSectionNameStyles(sections);

  const sxStyles = useMemo(
    () => ({ ...injectFieldNameStyles, ...injectSectionNameStyles }),
    [injectFieldNameStyles, injectSectionNameStyles]
  );

  const googleFontLinks = useMemo(
    () =>
      (visual.googleFonts ?? [])
        .filter(({ fontName }) => fontName.trim().length > 0)
        .filter(({ fontName }) => shouldLoadGoogleFont(fontName)),
    [visual.googleFonts]
  );

  const saveTemplateDebounced = useMemo(() => debounce(setTemplate, 500, { maxWait: 5000 }), []);

  const editorOptions: Partial<EditorOptions> = useMemo(() => {
    const extensions = [
      Highlight,
      Link,
      StarterKit,
      HeaderFooterSide,
      PageHeader,
      PageBody,
      PageFooter,
      Document.extend({ content: 'pageHeader pageBody pageFooter' }),
      Subscript,
      Superscript,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Color,
      Underline,
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      HtmlBlock,
      PageBreak,
      AlignBottom,
      Tabulator,
      DeviceLoop,
      IfBlock,
      InlinePlaceholder,
      BlockPlaceholder,
      DeviceFaultsPlaceholder,
      DeviceMeasurementsPlaceholder,
      StripFontFamilyOnPaste,
    ];

    if (FEATURE_TOGGLE_REVISION_TEMPLATE_EDITOR_FONT_STYLE) {
      extensions.push(TextStyle);
      extensions.push(FontFamily);
      extensions.push(FontSize);
    }

    return {
      extensions,
      autofocus: true,
      content: template.template,
      onUpdate: ({ editor }) => saveTemplateDebounced({ template: editor.getHTML() }),
      parseOptions: {
        preserveWhitespace: 'full',
      },
    };
  }, []);

  const editor = useEditor(editorOptions, []);

  usePlaceholderConfigurator(editor);
  useDeviceFaultsPlaceholderConfigurator(editor);
  useDeviceMeasurementsPlaceholderConfigurator(editor);

  return (
    <>
      {googleFontLinks.map(({ fontName, href }) => (
        <link key={fontName} rel="stylesheet" href={href} />
      ))}
      <style dangerouslySetInnerHTML={{ __html: visual.customCss }} />
      <RichTextEditor pos="relative" withTypographyStyles editor={editor} classNames={classes}>
        <Toolbar />
        <div className="grid grid-cols-[1fr]">
          <ScrollArea h="calc(100vh - 270px)">
            <RichTextEditor.Content ref={editorRef} sx={sxStyles} />
          </ScrollArea>
        </div>
        <IfBlockBubbleMenu />
        <DeviceLoopBubbleMenu />
        <TableMergeBubbleMenu />
      </RichTextEditor>
    </>
  );
}
